/**
 * PWA Utilities — cache clearing, service worker management, platform detection,
 * and Legend-State persistence cleanup.
 *
 * Extracted from Sidebar.jsx handleForceUpdate for reuse in VersionModal
 * and silent auto-update flows.
 *
 * Legend-State stores (articles, reading-progress, article-lists, etc.) persist
 * to localStorage. Content blobs and image caches use IndexedDB.
 * clearPersist() is the official API to wipe Legend-State persistence data and metadata.
 *
 * ALL cleanup paths (forceUpdate, forceCleanAndRelogin, auth$.logout) go through
 * cleanupAllBrowserData() to prevent drift. Never add cleanup logic outside it.
 */

import { syncState } from '@legendapp/state';
import { platform } from '../lib/native';
import { getAllPersistedStores } from '../store/storeRegistry';
import { clearContentCache, CONTENT_DB_NAME } from '../lib/contentCache';
import { clearCache as clearImageCache, IMAGE_CACHE_DB_NAME } from '../lib/imageCache';
import { stopContentFetcher } from '../lib/contentFetcher';

const FORCE_UPDATE_COOLDOWN_KEY = 'reedle-last-force-update';
const FORCE_UPDATE_COOLDOWN_MS = 60 * 1000; // 1 minute — prevents infinite reload loops
const CLEAR_PERSIST_TIMEOUT_MS = 5000; // 5 seconds — fallback if clearPersist hangs
const POST_CLEAR_DELAY_MS = 300; // Wait for async IDB deletes to flush before reload
const FORCE_FULL_SYNC_KEY = 'reedle-force-full-sync';

// ============================================
// Platform detection
// ============================================

/**
 * Detect if the app is running as an installed PWA (standalone mode)
 */
export function isPWA() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true // iOS Safari
  );
}

/**
 * Get the current platform label for display in the Version modal.
 * Returns: 'iOS' | 'Android' | 'PWA' | 'Web'
 */
export function getPlatformLabel() {
  if (platform.isIOS) return 'iOS';
  if (platform.isAndroid) return 'Android';
  if (isPWA()) return 'PWA';
  return 'Web';
}

// ============================================
// Low-level cleanup primitives
// ============================================

/**
 * Delete all Cache API entries (service worker precache + runtime caches)
 */
export async function clearAllCaches() {
  if (!('caches' in window)) return;
  try {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
    console.log('[pwaUtils] Cleared all caches:', names);
  } catch (e) {
    console.error('[pwaUtils] Failed to clear caches:', e);
  }
}

/**
 * Unregister all service worker registrations
 */
export async function unregisterAllServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
    console.log('[pwaUtils] Unregistered all service workers:', registrations.length);
  } catch (e) {
    console.error('[pwaUtils] Failed to unregister service workers:', e);
  }
}

/**
 * Attempt to delete IndexedDB databases directly — used ONLY as a fallback.
 * Wraps in a timeout to avoid hanging indefinitely.
 * Note: ReedleDB no longer exists — articles/reading-progress/article-lists
 * now persist to localStorage. Only content and image caches remain in IDB.
 */
export async function clearIndexedDB() {
  const DB_NAMES = [CONTENT_DB_NAME, IMAGE_CACHE_DB_NAME];
  for (const name of DB_NAMES) {
    try {
      const req = indexedDB.deleteDatabase(name);
      await Promise.race([
        new Promise((resolve, reject) => {
          req.onsuccess = resolve;
          req.onerror = reject;
          req.onblocked = () => {
            console.warn(`[pwaUtils] IndexedDB ${name} delete blocked by active connection`);
            resolve(); // don't hang — clearPersist should have handled it
          };
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('IndexedDB delete timed out')), CLEAR_PERSIST_TIMEOUT_MS)
        ),
      ]);
      console.log(`[pwaUtils] Deleted IndexedDB: ${name}`);
    } catch (e) {
      console.warn(`[pwaUtils] Failed to delete IndexedDB ${name}:`, e.message || e);
    }
  }
}

/**
 * Clear Legend-State localStorage sync markers, persistence metadata, and other app data.
 * Preserves language preference and auth token (auth token cleared separately when needed).
 */
export function clearLocalSyncData() {
  const keysToKeep = new Set(['reedle-ui-language', 'reedle-auth-token']);
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (keysToKeep.has(key)) continue;
    if (
      key.startsWith('reedle-') ||
      key.startsWith('__legend') ||
      key.startsWith('legend-')
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
  console.log('[pwaUtils] Cleared localStorage sync data:', keysToRemove);
}

// ============================================
// Legend-State persistence cleanup
// ============================================

/**
 * Clear all Legend-State store persistence using the official clearPersist() API.
 *
 * This is the ONLY reliable way to clear IndexedDB data when Legend-State holds
 * an active connection. Direct indexedDB.deleteDatabase() will be blocked.
 *
 * clearPersist() clears BOTH:
 *   - The persisted data (articles, highlights, etc.)
 *   - The sync metadata (lastSync timestamps used by changesSince: 'last-sync')
 *
 * @returns {boolean} true if clearPersist succeeded, false if fallback needed
 */
export async function clearAllStoresPersistence() {
  try {
    const allStores = await getAllPersistedStores();
    console.log('[pwaUtils] Clearing persistence on', allStores.length, 'stores...');
    let anyFailed = false; // Track failures to trigger fallback

    // NOTE: We intentionally do NOT call isSyncEnabled.set(false) / isPersistEnabled.set(false)
    // here. Those reactive mutations trigger Legend-State re-renders on subscribed components,
    // which can crash the React tree before window.location.reload() fires — causing
    // the "Something went wrong" error boundary to appear instead of a clean reload.
    // clearPersist() handles stopping persistence internally; the brief window of in-flight
    // writes is harmless since we're about to wipe everything and reload.

    // Call clearPersist() on each store with a timeout guard
    // clearPersist can be undefined if persistence hasn't loaded yet (Legend-State #500)
    const clearPromises = allStores.map(async (store$) => {
      try {
        const state$ = syncState(store$);
        if (typeof state$?.clearPersist === 'function') {
          await Promise.race([
            state$.clearPersist(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('clearPersist timed out')), CLEAR_PERSIST_TIMEOUT_MS)
            ),
          ]);
        } else {
          // Store doesn't have clearPersist - either no persistence configured or not loaded yet
          // This is OK, not a failure - nothing to clear for this store
          // Don't mark anyFailed=true here; only actual errors should trigger fallback
          console.log('[pwaUtils] clearPersist not available on a store (no persistence configured)');
        }
      } catch (e) {
        console.warn('[pwaUtils] clearPersist failed on a store:', e.message || e);
        anyFailed = true; // Mark as failed so fallback runs
      }
    });
    await Promise.all(clearPromises);

    console.log('[pwaUtils] Legend-State clearPersist complete, anyFailed:', anyFailed);
    return !anyFailed; // Return false if any store failed, so fallback clearIndexedDB runs
  } catch (e) {
    console.error('[pwaUtils] clearAllStoresPersistence failed:', e);
    return false;
  }
}

// ============================================
// Consolidated cleanup — single source of truth
// ============================================

/**
 * Clear ALL browser-persisted data in one function.
 *
 * This is the ONLY place cleanup logic should live. forceUpdate(),
 * forceCleanAndRelogin(), and auth$.logout() all delegate here.
 * When adding a new store or IDB database, update this function ONLY.
 *
 * @param {object} options
 * @param {boolean} options.keepAuth - If true, preserve auth token (for soft update)
 */
export async function cleanupAllBrowserData({ keepAuth = false } = {}) {
  try {
    // 1. Legend-State: clearPersist on all 14 stores (localStorage + any remaining IDB)
    const cleared = await clearAllStoresPersistence();
    if (!cleared) {
      // Fallback: direct IDB delete for content/image caches
      console.warn('[pwaUtils] clearPersist failed, attempting direct IndexedDB delete...');
      await clearIndexedDB();
    }

    // 2. Clear separate content cache (ReedleContentDB) — store.clear() + reset in-memory ID cache
    await clearContentCache();

    // 3. Clear image cache (reedle-image-cache) — store.clear() via open connection
    try {
      await clearImageCache();
      console.log('[pwaUtils] Image cache cleared');
    } catch (e) {
      console.warn('[pwaUtils] Image cache clear failed:', e.message || e);
    }

    // 4. Clear localStorage sync data (all reedle-*/legend-* keys except language + auth)
    clearLocalSyncData();

    // 5. If full clean (logout / force clean), also remove auth token
    if (!keepAuth) {
      localStorage.removeItem('reedle-auth-token');
    }

    // 6. Clear service worker caches (Cache API)
    await clearAllCaches();

    // 7. Unregister service workers
    await unregisterAllServiceWorkers();
  } catch (e) {
    console.error('[pwaUtils] Error during cleanup:', e);
  }

  // 8. Set force-full-sync flag so next boot does a full sync
  // Safety net: even if some lastSync metadata survived the async IDB delete,
  // this flag forces changesSince: undefined → full data refetch
  localStorage.setItem(FORCE_FULL_SYNC_KEY, 'true');
}

// ============================================
// High-level update/cleanup operations
// ============================================

/**
 * Force update: clear persistence + caches + unregister SW + reload.
 * For web/PWA "soft" update — user stays logged in.
 */
export async function forceUpdate() {
  // Signal to ErrorBoundary: we're intentionally tearing down — show "Updating..."
  // instead of the error page if cleanup's reactive side effects crash React.
  window.__reedle_navigating = true;

  // Stop background content fetcher BEFORE clearing storage — prevents in-flight
  // Supabase requests from writing to IDB after it's been wiped.
  stopContentFetcher();

  await cleanupAllBrowserData({ keepAuth: true });

  // Small delay to let async cleanup flush before reload.
  await new Promise((resolve) => setTimeout(resolve, POST_CLEAR_DELAY_MS));

  window.location.reload();
}

/**
 * Nuclear option: wipe everything + redirect to login.
 * Clears Legend-State persistence, localStorage sync data, caches, SW, and auth token.
 */
export async function forceCleanAndRelogin() {
  // Signal to ErrorBoundary: we're intentionally tearing down — show "Updating..."
  window.__reedle_navigating = true;

  // Stop background content fetcher BEFORE clearing storage
  stopContentFetcher();

  await cleanupAllBrowserData({ keepAuth: false });

  // Small delay to let async cleanup flush
  await new Promise((resolve) => setTimeout(resolve, POST_CLEAR_DELAY_MS));

  window.location.href = '/login';
}

// ============================================
// Service worker update check
// ============================================

/**
 * Trigger a manual service worker update check.
 * Returns true if a registration was found and update() was called.
 */
export async function checkForSWUpdate() {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    console.log('[pwaUtils] Triggered SW update check');
    return true;
  } catch (e) {
    console.warn('[pwaUtils] SW update check failed:', e);
    return false;
  }
}

// ============================================
// Cooldown / anti-loop safeguards
// ============================================

/**
 * Check if a force update was attempted recently (anti-loop safeguard).
 * Used by silent auto-update to prevent infinite reload loops.
 */
export function isForceUpdateOnCooldown() {
  const last = localStorage.getItem(FORCE_UPDATE_COOLDOWN_KEY);
  if (!last) return false;
  return Date.now() - parseInt(last, 10) < FORCE_UPDATE_COOLDOWN_MS;
}

/**
 * Record that a force update was just attempted.
 */
export function recordForceUpdate() {
  localStorage.setItem(FORCE_UPDATE_COOLDOWN_KEY, String(Date.now()));
}

/**
 * Silent force update with cooldown protection.
 * Used when remote config says force_update=true.
 */
export async function silentForceUpdate() {
  if (isForceUpdateOnCooldown()) {
    console.log('[pwaUtils] Force update on cooldown, skipping');
    return;
  }
  recordForceUpdate();
  await forceUpdate();
}
